import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { storage } from '../../utils/storage';
import './InstallPrompt.css';

/**
 * Banner para promover la instalación de la PWA
 */
function InstallPrompt({
  canInstall,
  isInstalling,
  onInstall,
  onDismiss
}) {
  const { t } = useTranslation();
  const [dismissed, setDismissed] = useState(false);
  const [visible, setVisible] = useState(false);

  // Mostrar después de un delay para no ser intrusivo
  useEffect(() => {
    if (!canInstall || dismissed) return;

    // Check if was dismissed recently
    const dismissedTime = storage.get('pwa-install-dismissed');
    if (dismissedTime) {
      const dayInMs = 24 * 60 * 60 * 1000;
      if (Date.now() - parseInt(dismissedTime, 10) < dayInMs) {
        setDismissed(true);
        return;
      }
    }

    // Show after 5 seconds
    const timer = setTimeout(() => {
      setVisible(true);
    }, 5000);

    return () => clearTimeout(timer);
  }, [canInstall, dismissed]);

  const handleDismiss = () => {
    setVisible(false);
    setDismissed(true);
    storage.set('pwa-install-dismissed', Date.now().toString());
    onDismiss?.();
  };

  const handleInstall = async () => {
    const result = await onInstall();
    if (result?.outcome === 'accepted') {
      setVisible(false);
    }
  };

  if (!visible || !canInstall || dismissed) {
    return null;
  }

  return (
    <div className="install-prompt">
      <div className="install-prompt__content">
        <div className="install-prompt__icon">
          <span className="material-icons" aria-hidden="true">get_app</span>
        </div>
        <div className="install-prompt__text">
          <h3 className="install-prompt__title">
            {t('pwa.installTitle', 'Instalar App')}
          </h3>
          <p className="install-prompt__description">
            {t('pwa.installDescription', 'Accede al menú sin navegador')}
          </p>
        </div>
        <div className="install-prompt__actions">
          <button
            className="install-prompt__btn-install"
            onClick={handleInstall}
            disabled={isInstalling}
          >
            {isInstalling ? (
              <span className="material-icons install-prompt__spinner" aria-hidden="true">sync</span>
            ) : (
              t('pwa.install', 'Instalar')
            )}
          </button>
          <button
            className="install-prompt__btn-dismiss"
            onClick={handleDismiss}
            aria-label={t('common.close')}
          >
            <span className="material-icons" aria-hidden="true">close</span>
          </button>
        </div>
      </div>
    </div>
  );
}

export default InstallPrompt;
